import { createInertiaApp } from '@inertiajs/react';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { Toaster } from '@/theme1/components/ui/sonner';
import { TooltipProvider } from '@/theme1/components/ui/tooltip';
import { CommerceProvider } from '@/contexts/CommerceProvider';
import { initializeTheme } from '@/hooks/use-appearance';
import AppLayout from '@/layouts/app-layout';
import Theme1AuthLayout from '@/theme1/layouts/auth-layout';
import Theme2AuthLayout from '@/theme2/layouts/auth-layout';
import Theme1StoreLayout from '@/theme1/layouts/StoreLayout';
import Theme2StoreLayout from '@/theme2/layouts/StoreLayout';
import Theme3StoreLayout from '@/theme3/layouts/StoreLayout';
import SettingsLayout from '@/layouts/settings/layout';

const appName = import.meta.env.VITE_APP_NAME || 'Captex';
const activeTheme =
    typeof document !== 'undefined'
        ? document.documentElement.dataset.frontendTheme || 'theme1'
        : 'theme1';

const themePageGlobs = {
    theme1: {
        jsx: import.meta.glob('./theme1/pages/**/*.jsx'),
        tsx: import.meta.glob('./theme1/pages/**/*.tsx'),
    },
    theme2: {
        jsx: import.meta.glob('./theme2/pages/**/*.jsx'),
        tsx: import.meta.glob('./theme2/pages/**/*.tsx'),
    },
    theme3: {
        jsx: import.meta.glob('./theme3/pages/**/*.jsx'),
        tsx: import.meta.glob('./theme3/pages/**/*.tsx'),
    },
};

function resolveThemePage(theme, name) {
    const themePages = themePageGlobs[theme];

    for (const extension of ['tsx', 'jsx']) {
        const path = `./${theme}/pages/${name}.${extension}`;

        if (themePages[extension][path]) {
            return resolvePageComponent(path, themePages[extension]);
        }
    }

    return null;
}

createInertiaApp({
    title: (title) => (title ? `${title}` : appName),
    resolve: async (name) => {
        const themeOrder = [
            activeTheme,
            ...['theme1', 'theme2', 'theme3'].filter((theme) => theme !== activeTheme),
        ];

        for (const theme of themeOrder) {
            const resolved = resolveThemePage(theme, name);

            if (resolved) {
                return resolved;
            }
        }

        throw new Error(`Page not found: ${name}`);
    },
    layout: (name) => {
        const storePages = ['home', 'caps', 'collections', 'new-in', 'flash-deals', 'apparel'];
        const normalizedName = name.toLowerCase();
        const storeLayout =
            activeTheme === 'theme3'
                ? Theme3StoreLayout
                : activeTheme === 'theme2'
                    ? Theme2StoreLayout
                    : Theme1StoreLayout;
        const authLayout =
            activeTheme === 'theme2'
                ? Theme2AuthLayout
                : Theme1AuthLayout;

        if (storePages.includes(normalizedName) || name.startsWith('store/')) {
            return storeLayout;
        }

        const adminPages = ['dashboard', 'admin', 'users', 'products', 'orders'];

        if (adminPages.includes(normalizedName) || name.startsWith('admin/')) {
            return AppLayout;
        }

        if (name === 'auth/login') {
            return activeTheme === 'theme3' ? storeLayout : authLayout;
        }

        if (name === 'auth/register') {
            return activeTheme === 'theme3' ? storeLayout : authLayout;
        }

        if (name === 'auth/forgot-password') {
            return activeTheme === 'theme3' ? storeLayout : authLayout;
        }

        if (name.startsWith('auth/')) {
            return activeTheme === 'theme3' ? storeLayout : authLayout;
        }

        if (name.startsWith('settings/')) {
            return SettingsLayout;
        }

        if (name.startsWith('account/')) {
            return SettingsLayout;
        }

        return storeLayout;
    },
    strictMode: true,
    withApp(app, { page }) {
        return (
            <TooltipProvider delayDuration={0}>
                <CommerceProvider initialCommerce={page.props.commerce}>
                    {app}
                </CommerceProvider>
                <Toaster />
            </TooltipProvider>
        );
    },
    progress: {
        color: '#4B5563',
    },
});

initializeTheme();
